/**
 * <mb-producto-granel>: ficha de granel, cajas y snacks (sections/mb-producto-granel.liquid).
 *
 * Sin JavaScript la ficha sigue funcionando (formulario POST /cart/add con la variante en el radio «id» y la cantidad en «quantity»).
 * Este componente añade:
 * - Cantidad: botones −/+ y campo numérico (mínimo 1).
 * - Tramos (solo si el producto está en la colección con descuentos): botones que ponen su cantidad (aria-pressed en el alcanzado),
 *   precio por unidad con descuento + original tachado, píldora «Ahorras X € (−N %)», aviso de lo que falta y total del botón con el sin descuento tachado. Es una ayuda visual: el descuento real lo aplican los descuentos automáticos de Shopify en el carrito.
 * - Variantes: un grupo de botones por opción (la variante sale de la combinación), precio, tachado, ahorro, €/kg y €/bolsa, ?variant= y evento `mb:variante`.
 * - Compra: POST /cart/add.js y apertura del carrito lateral de Horizon con CartLinesUpdateEvent (acción `add`).
 * - Personalización (opcional, campo [data-personalizado]): quita emojis, cuenta caracteres y valida en español. Viaja en el FormData como properties[...].
 * - Barra fija móvil: aparece cuando el botón principal se queda por encima de la pantalla.
 *
 * Evento publicado (contrato con mb-envio): document `mb:variante`,
 * detail = { variante_id, peso_g, precio_cents, cantidad }, al cargar y en cada cambio de variante o de cantidad.
 * El último detalle queda también en window.mbVariante por si un script llega tarde.
 */

const CANTIDAD_MAX = 999;

/** Importes en es-ES con el € detrás: «41,50 €». */
const DINERO = new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' });

/** Emojis y modificadores: no caben en la personalización (se imprime/escribe tal cual). */
const EMOJI = /[\p{Extended_Pictographic}\p{Emoji_Modifier}\p{Regional_Indicator}\uFE0F\u200D\u20E3]/gu;

const raiz = () => window.Shopify?.routes?.root ?? '/';

/** Ejecuta `fn` cuando ya han corrido todos los módulos de la página (los demás scripts ya escuchan). */
function alListo(fn) {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => fn(), { once: true });
  } else {
    setTimeout(fn, 0);
  }
}

/** Error devuelto por la tienda al añadir (agotado, límite de stock...). */
class ErrorTienda extends Error {}

/** @param {any} carrito */
function publicarCarrito(carrito) {
  window.mbCarrito = carrito;
  document.dispatchEvent(new CustomEvent('mb:carrito', { detail: carrito }));
}

class MbProductoGranel extends HTMLElement {
  /** @type {AbortController | null} */
  #controller = null;
  /** @type {HTMLFormElement | null} */
  #form = null;
  /** @type {HTMLInputElement | null} */
  #campo = null;
  /** @type {HTMLElement | null} */
  #boton = null;
  /** @type {HTMLElement | null} */
  #barra = null;
  /** @type {{ unidades: number, descuento: number }[]} */
  #tramos = [];
  #cantidad = 1;
  #ahorroAnunciado = 0;
  #ocupado = false;
  /** @type {typeof import('@shopify/events') | null} */
  #eventos = null;

  connectedCallback() {
    this.#form = this.querySelector('[data-formulario]');
    this.#campo = this.querySelector('[data-cantidad]');
    this.#boton = this.querySelector('[data-enviar]');
    this.#barra = this.querySelector('[data-barra]');
    this.#controller = new AbortController();

    this.#tramos = (this.dataset.tramos ?? '')
      .split('|')
      .map((par) => par.split(':').map((n) => Number(n.replace(',', '.'))))
      .filter(([u, d]) => u >= 2 && d > 0)
      .map(([unidades, descuento]) => ({ unidades, descuento }))
      .sort((a, b) => a.unidades - b.unidades);

    this.#cantidadControles();
    this.#variantes();
    this.#personalizado();
    this.#compra();
    this.#barraFija();

    import('@shopify/events')
      .then((modulo) => {
        this.#eventos = modulo;
      })
      .catch(() => {
        this.#eventos = null;
      });

    this.#pintar();
    alListo(() => this.#anunciarVariante());
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#controller = null;
  }

  get #signal() {
    return /** @type {AbortController} */ (this.#controller).signal;
  }

  /** Datos de la variante elegida: el campo oculto «id», que el JS rellena al combinar las opciones. */
  get #fuente() {
    const fuente = this.querySelector('input[data-fuente]');
    return fuente instanceof HTMLInputElement ? fuente : null;
  }

  // ---------------------------------------------------------------- Cantidad

  #cantidadControles() {
    const campo = this.#campo;
    if (!campo) return;

    /** @param {number} valor */
    const fijar = (valor) => {
      this.#cantidad = Math.max(1, Math.min(CANTIDAD_MAX, Math.round(valor) || 1));
      campo.value = String(this.#cantidad);
      this.#alCambiarCantidad();
    };

    this.querySelector('[data-menos]')?.addEventListener('click', () => fijar(this.#cantidad - 1), { signal: this.#signal });
    this.querySelector('[data-mas]')?.addEventListener('click', () => fijar(this.#cantidad + 1), { signal: this.#signal });

    // Mientras se escribe se calcula con lo que haya; al salir del campo se normaliza
    campo.addEventListener(
      'input',
      () => {
        const valor = Math.round(Number(campo.value));
        this.#cantidad = Number.isFinite(valor) && valor >= 1 ? Math.min(CANTIDAD_MAX, valor) : 1;
        this.#alCambiarCantidad();
      },
      { signal: this.#signal }
    );
    campo.addEventListener('change', () => fijar(Number(campo.value)), { signal: this.#signal });
    campo.addEventListener(
      'keydown',
      (evento) => {
        if (evento.key !== 'Enter') return;
        evento.preventDefault();
        campo.blur();
      },
      { signal: this.#signal }
    );

    // Pulsar un tramo pone esa cantidad
    for (const tramo of this.querySelectorAll('[data-tramo-unidades]')) {
      tramo.addEventListener(
        'click',
        () => fijar(Number(/** @type {HTMLElement} */ (tramo).dataset.tramoUnidades)),
        { signal: this.#signal }
      );
    }
  }

  #alCambiarCantidad() {
    this.#pintar();
    this.#anunciarVariante();
  }

  /** Tramo alcanzado con la cantidad actual (o null). */
  get #tramoAlcanzado() {
    let alcanzado = null;
    for (const tramo of this.#tramos) {
      if (this.#cantidad >= tramo.unidades) alcanzado = tramo;
    }
    return alcanzado;
  }

  /**
   * Dinero en es-ES con el € detrás del importe («41,50 €»).
   * @param {number} centimos
   */
  #dinero(centimos) {
    return DINERO.format(centimos / 100);
  }

  /**
   * Precio por unidad con descuento: «9,02 €/kg» con 92 % → «8,30 €/kg». Conserva el sufijo («/kg», «/bolsa»).
   * @param {string} texto - Texto original del servidor (vacío si no hay precio por unidad)
   * @param {number} porcentajeQueda - 100 − descuento del tramo
   */
  #unidadConDescuento(texto, porcentajeQueda) {
    const numero = texto.match(/\d+(?:\.\d{3})*(?:,\d+)?/);
    const barra = texto.lastIndexOf('/');
    if (!numero || barra === -1) return texto;
    const centimos = Math.round(Math.round(Number(numero[0].replace(/\./g, '').replace(',', '.')) * 100) * porcentajeQueda / 100);
    return `${this.#dinero(centimos)}${texto.slice(barra)}`;
  }

  /**
   * Precio, ahorro, total del botón y estado de los tramos.
   * OJO: todo esto es una ayuda visual. El descuento real lo aplican los descuentos automáticos de Shopify (BUNDLEx) en el
   * carrito, y solo si el producto está en la colección de los tramos (la fila de tramos solo sale en ese caso).
   */
  #pintar() {
    const fuente = this.#fuente;
    if (!fuente) return;
    const unitario = Number(fuente.dataset.precioCents) || 0;
    const alcanzado = this.#tramoAlcanzado;
    const descuento = alcanzado?.descuento ?? 0;
    const bruto = unitario * this.#cantidad;
    const ahorro = Math.round((bruto * descuento) / 100);
    const total = bruto - ahorro;
    const conTramo = ahorro > 0;

    // Total del botón (y de la barra fija), con el total sin descuento tachado
    for (const salida of this.querySelectorAll('[data-total]')) {
      salida.textContent = this.#dinero(total);
    }
    const antes = this.querySelector('[data-total-antes]');
    const antesSr = this.querySelector('[data-total-antes-sr]');
    if (antes) {
      antes.textContent = conTramo ? this.#dinero(bruto) : '';
      antes.toggleAttribute('hidden', !conTramo);
    }
    if (antesSr) {
      antesSr.textContent = conTramo ? ` antes ${this.#dinero(bruto)}` : '';
      antesSr.toggleAttribute('hidden', !conTramo);
    }

    // Precio grande: por unidad con descuento, y el original tachado al lado
    const precio = this.querySelector('[data-precio]:not(input)');
    const sufijo = this.querySelector('[data-precio-sufijo]');
    const original = this.querySelector('[data-precio-original]');
    const originalValor = this.querySelector('[data-precio-original-valor]');
    if (precio) {
      precio.textContent = conTramo ? this.#dinero(Math.round((unitario * (100 - descuento)) / 100)) : (fuente.dataset.precio ?? '');
    }
    if (sufijo) {
      const unidad = (this.dataset.palabra ?? 'bolsas').replace(/s$/, '');
      sufijo.textContent = conTramo ? `/${unidad}` : '';
      sufijo.toggleAttribute('hidden', !conTramo);
    }
    if (original && originalValor) {
      originalValor.textContent = conTramo ? this.#dinero(unitario) : '';
      original.toggleAttribute('hidden', !conTramo);
    }
    // €/kg y €/bolsa: con tramo, el mismo factor que el precio por unidad; sin tramo, el texto original
    for (const [selector, original] of [
      ['[data-unidad-kg]:not(input)', fuente.dataset.unidadKg ?? ''],
      ['[data-unidad-bolsa]:not(input)', fuente.dataset.unidadBolsa ?? ''],
    ]) {
      const salida = this.querySelector(selector);
      if (!(salida instanceof HTMLElement)) continue;
      const texto = conTramo ? this.#unidadConDescuento(original, 100 - descuento) : original;
      salida.textContent = texto;
      salida.toggleAttribute('hidden', texto === '');
    }
    this.querySelector('.mb-pgranel__precio-bloque')?.toggleAttribute('data-tramo', conTramo);

    // Píldora «Ahorras X € (−N %)»: se anima (CSS, desactivado con prefers-reduced-motion) solo cuando cambia
    const pildora = this.querySelector('[data-ahorro-tramo]');
    if (pildora instanceof HTMLElement) {
      const texto = conTramo ? `Ahorras ${this.#dinero(ahorro)} (−${descuento} %)` : '';
      if (pildora.textContent !== texto) {
        pildora.textContent = texto;
        pildora.toggleAttribute('hidden', !conTramo);
        pildora.removeAttribute('data-pop');
        if (conTramo) {
          void pildora.offsetWidth; // reinicia la animación
          pildora.setAttribute('data-pop', '');
        }
      }
    }
    // Aviso para lectores de pantalla: solo cuando cambia el importe ahorrado
    if (ahorro !== this.#ahorroAnunciado) {
      this.#ahorroAnunciado = ahorro;
      const region = this.querySelector('[data-anuncio-ahorro]');
      if (region) region.textContent = conTramo ? `Ahorras ${this.#dinero(ahorro)}` : '';
    }

    if (this.#tramos.length === 0) return;

    for (const boton of this.querySelectorAll('[data-tramo-unidades]')) {
      const elegido = alcanzado !== null && Number(/** @type {HTMLElement} */ (boton).dataset.tramoUnidades) === alcanzado.unidades;
      boton.toggleAttribute('data-alcanzado', elegido);
      boton.setAttribute('aria-pressed', String(elegido));
    }

    const aviso = this.querySelector('[data-aviso-tramo]');
    if (!aviso) return;
    const siguiente = this.#tramos.find((tramo) => tramo.unidades > this.#cantidad);
    if (siguiente) {
      aviso.textContent = `Añade ${siguiente.unidades - this.#cantidad} más y ahorra ${siguiente.descuento} %`;
    } else if (alcanzado) {
      aviso.textContent = `Tienes el máximo: −${alcanzado.descuento} %`;
    } else {
      aviso.textContent = '';
    }
  }

  // --------------------------------------------------------------- Variantes

  /** Variantes del producto (elementos ocultos con sus datos y los valores de cada opción). */
  get #listaVariantes() {
    return [...this.querySelectorAll('[data-variante]')].filter((el) => el instanceof HTMLElement);
  }

  /** Valores marcados, uno por opción (en orden). */
  #seleccion() {
    const valores = [];
    for (const radio of this.querySelectorAll('input[type="radio"][data-opcion]:checked')) {
      valores[Number(/** @type {HTMLInputElement} */ (radio).dataset.opcion) - 1] = /** @type {HTMLInputElement} */ (radio).value;
    }
    return valores;
  }

  /**
   * @param {HTMLElement} variante
   * @param {number} posicion Posición (desde 1)
   */
  #valorDe(variante, posicion) {
    return variante.dataset[`o${posicion}`] ?? '';
  }

  /**
   * Variante que resulta de poner `valor` en la opción `posicion` manteniendo el resto de la selección.
   * Si esa combinación no existe, la que más opciones conserva (con preferencia por las disponibles).
   * @param {number} posicion
   * @param {string} valor
   * @returns {HTMLElement | null}
   */
  #resolver(posicion, valor) {
    const seleccion = this.#seleccion();
    seleccion[posicion - 1] = valor;
    let mejor = null;
    let mejorPuntos = -1;
    for (const variante of this.#listaVariantes) {
      if (this.#valorDe(variante, posicion) !== valor) continue;
      let puntos = 0;
      for (let i = 1; i <= seleccion.length; i++) {
        if (this.#valorDe(variante, i) === seleccion[i - 1]) puntos += 1;
      }
      const exacta = puntos === seleccion.length;
      puntos = (exacta ? 1000 : 0) + (variante.dataset.disponible === 'true' ? 100 : 0) + puntos;
      if (puntos > mejorPuntos) {
        mejor = variante;
        mejorPuntos = puntos;
      }
    }
    return mejor;
  }

  /** Precio y disponibilidad de cada botón según la combinación que resultaría al pulsarlo. */
  #pintarOpciones() {
    for (const radio of this.querySelectorAll('input[type="radio"][data-opcion]')) {
      if (!(radio instanceof HTMLInputElement)) continue;
      const variante = this.#resolver(Number(radio.dataset.opcion), radio.value);
      const salida = radio.closest('label')?.querySelector('[data-opcion-precio]');
      if (!variante) continue;
      const disponible = variante.dataset.disponible === 'true';
      radio.disabled = !disponible;
      if (salida) salida.textContent = disponible ? (variante.dataset.precio ?? '') : 'Agotado';
    }
  }

  /** Copia los datos de la variante elegida al campo oculto «id». */
  #aplicarVariante() {
    const fuente = this.#fuente;
    const seleccion = this.#seleccion();
    if (!fuente) return false;
    const elegida = this.#listaVariantes.find((variante) => seleccion.every((valor, i) => this.#valorDe(variante, i + 1) === valor));
    if (!elegida) return false;
    fuente.value = elegida.dataset.variante ?? fuente.value;
    for (const clave of ['peso', 'precioCents', 'precio', 'compara', 'ahorro', 'unidadKg', 'unidadBolsa', 'disponible']) {
      fuente.dataset[clave] = elegida.dataset[clave] ?? '';
    }
    return true;
  }

  #variantes() {
    this.#pintarOpciones();
    for (const radio of this.querySelectorAll('input[type="radio"][data-opcion]')) {
      radio.addEventListener(
        'change',
        () => {
          if (!(radio instanceof HTMLInputElement)) return;
          // Si la combinación exacta no existe, se adoptan también las demás opciones de la variante más cercana
          const variante = this.#resolver(Number(radio.dataset.opcion), radio.value);
          if (!variante) return;
          for (const otro of this.querySelectorAll('input[type="radio"][data-opcion]')) {
            if (otro instanceof HTMLInputElement) otro.checked = otro.value === this.#valorDe(variante, Number(otro.dataset.opcion));
          }
          if (this.#aplicarVariante()) {
            this.#pintarOpciones();
            this.#alCambiarVariante();
          }
        },
        { signal: this.#signal }
      );
    }
  }

  /**
   * @param {string} selector
   * @param {string} texto
   */
  #texto(selector, texto) {
    const elemento = this.querySelector(`${selector}:not(input)`);
    if (!elemento) return;
    if (selector === '[data-compara]') {
      const oculto = document.createElement('span');
      oculto.className = 'visually-hidden';
      oculto.textContent = 'Precio anterior ';
      elemento.replaceChildren(oculto, document.createTextNode(texto));
    } else {
      elemento.textContent = texto;
    }
    elemento.toggleAttribute('hidden', texto === '');
  }

  #alCambiarVariante() {
    const fuente = this.#fuente;
    if (!fuente) return;
    const d = fuente.dataset;

    this.#texto('[data-compara]', d.compara ?? '');
    this.#texto('[data-ahorro]', d.ahorro ?? '');
    this.#texto('[data-unidad-kg]', d.unidadKg ?? '');
    this.#texto('[data-unidad-bolsa]', d.unidadBolsa ?? '');

    const url = new URL(window.location.href);
    url.searchParams.set('variant', fuente.value);
    window.history.replaceState(window.history.state, '', url);

    this.#pintar();
    this.#anunciarVariante();
  }

  #anunciarVariante() {
    const fuente = this.#fuente;
    if (!fuente) return;
    const detalle = {
      variante_id: Number(fuente.value),
      peso_g: Number(fuente.dataset.peso) || 0,
      media_id: Number(fuente.dataset.media) || 0,
      precio_cents: Number(fuente.dataset.precioCents) || 0,
      cantidad: this.#cantidad,
    };
    window.mbVariante = detalle;
    document.dispatchEvent(new CustomEvent('mb:variante', { detail: detalle }));
  }

  // ------------------------------------------------------------------ Compra

  /** @param {string} mensaje */
  #anunciar(mensaje) {
    const region = this.querySelector('[data-anuncio]');
    if (!region) return;
    region.textContent = '';
    requestAnimationFrame(() => {
      region.textContent = mensaje;
    });
  }

  /** @param {boolean} ocupado */
  #marcarOcupado(ocupado) {
    this.#ocupado = ocupado;
    const texto = this.querySelector('[data-enviar-texto]');
    const botonBarra = this.#barra?.querySelector('[data-barra-enviar]');
    if (ocupado) {
      this.#boton?.setAttribute('aria-busy', 'true');
      botonBarra?.setAttribute('aria-busy', 'true');
      if (texto instanceof HTMLElement) {
        texto.dataset.original = texto.innerHTML;
        texto.textContent = 'Añadiendo…';
      }
    } else {
      this.#boton?.removeAttribute('aria-busy');
      botonBarra?.removeAttribute('aria-busy');
      if (texto instanceof HTMLElement && texto.dataset.original) {
        texto.innerHTML = texto.dataset.original;
        delete texto.dataset.original;
      }
    }
  }

  /** Abre el carrito lateral de Horizon (su ajuste «abrir al añadir» está apagado en este tema). */
  async #abrirCarrito() {
    const cajon = document.getElementById('cart-drawer');
    if (!cajon) return;
    await customElements.whenDefined('theme-drawer');
    if (!cajon.isOpen) cajon.open();
  }

  /** Campo de texto obligatorio de personalización (regalos con nombre). Sin él en la ficha, no hace nada. */
  #personalizado() {
    const campo = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-personalizado]'));
    if (!campo) return;
    const contador = this.querySelector('[data-personalizado-contador]');
    const nota = this.querySelector('[data-personalizado-nota]');
    const error = this.querySelector('[data-personalizado-error]');
    const mensajeVacio = this.dataset.msgVacio ?? 'Escribe el texto para personalizarlo';
    const mensajeEmoji = this.dataset.msgEmoji ?? '';
    const largoMax = campo.maxLength > 0 ? campo.maxLength : 30;
    let componiendo = false;
    /** @type {number | undefined} */
    let temporizador;

    const contar = () => {
      if (contador) contador.textContent = `${campo.value.length}/${largoMax}`;
    };

    const quitarEmojis = () => {
      const limpio = campo.value.replace(EMOJI, '');
      if (limpio === campo.value) return;
      const quitados = campo.value.length - limpio.length;
      const cursor = Math.max(0, (campo.selectionStart ?? limpio.length) - quitados);
      campo.value = limpio;
      campo.setSelectionRange(cursor, cursor);
      if (nota && mensajeEmoji) {
        clearTimeout(temporizador);
        nota.textContent = '';
        requestAnimationFrame(() => {
          nota.textContent = mensajeEmoji;
        });
        temporizador = window.setTimeout(() => {
          nota.textContent = '';
        }, 5000);
      }
    };

    campo.addEventListener('compositionstart', () => (componiendo = true), { signal: this.#signal });
    campo.addEventListener(
      'compositionend',
      () => {
        componiendo = false;
        quitarEmojis();
        contar();
      },
      { signal: this.#signal }
    );
    campo.addEventListener(
      'input',
      () => {
        if (!componiendo) quitarEmojis();
        // Solo espacios no cuenta: se trata como vacío (el navegador bloquea el envío y mueve el foco al campo)
        campo.setCustomValidity(campo.value.length > 0 && !campo.value.trim() ? mensajeVacio : '');
        campo.removeAttribute('aria-invalid');
        if (error) error.textContent = '';
        contar();
      },
      { signal: this.#signal }
    );

    // Espacios sobrantes de los extremos fuera antes de enviar
    this.#form?.addEventListener(
      'submit',
      () => {
        const limpio = campo.value.replace(/\s+/g, ' ').trim();
        if (limpio !== campo.value) campo.value = limpio;
      },
      { signal: this.#signal, capture: true }
    );
    campo.addEventListener(
      'invalid',
      () => {
        campo.setCustomValidity(mensajeVacio);
        campo.setAttribute('aria-invalid', 'true');
        if (error) error.textContent = mensajeVacio;
      },
      { signal: this.#signal }
    );

    // Enter cierra el teclado en vez de enviar sin revisar el resto de opciones
    campo.addEventListener(
      'keydown',
      (evento) => {
        if (evento.key !== 'Enter') return;
        evento.preventDefault();
        campo.blur();
      },
      { signal: this.#signal }
    );

    window.addEventListener('pageshow', () => contar(), { signal: this.#signal });
    contar();
  }

  #compra() {
    const form = this.#form;
    if (!form) return;
    form.addEventListener('submit', (evento) => this.#enviar(evento), { signal: this.#signal });
  }

  /** @param {SubmitEvent} evento */
  async #enviar(evento) {
    const form = this.#form;
    if (!form) return;
    evento.preventDefault();
    if (this.#ocupado) return;

    if (this.#campo) this.#campo.value = String(this.#cantidad);

    this.#marcarOcupado(true);
    const errorEnvio = this.querySelector('[data-error-envio]');
    if (errorEnvio) errorEnvio.textContent = '';

    const datos = new FormData(form);
    // Secciones del carrito que Horizon refresca con la propia respuesta de /cart/add.js
    const secciones = [...document.querySelectorAll('cart-items-component')]
      .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : ''))
      .filter(Boolean);
    if (secciones.length > 0) datos.set('sections', secciones.join(','));

    const { CartLinesUpdateEvent, CartErrorEvent } = this.#eventos ?? {};
    const diferido = CartLinesUpdateEvent?.createPromise?.();
    if (diferido && CartLinesUpdateEvent) {
      // Si nadie atiende la promesa no debe saltar un error no capturado
      diferido.promise.catch(() => {});
      this.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'add',
          context: 'product',
          lines: [{ merchandiseId: String(datos.get('id')), quantity: this.#cantidad }],
          promise: diferido.promise,
        })
      );
    }

    try {
      const respuesta = await fetch(window.Theme?.routes?.cart_add_url ?? `${raiz()}cart/add.js`, {
        method: 'POST',
        body: datos,
        headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' },
      });
      const cuerpo = await respuesta.json();
      if (!respuesta.ok || cuerpo.status) {
        throw new ErrorTienda(cuerpo.description || cuerpo.message || 'No se pudo añadir');
      }

      const carritoRespuesta = await fetch(`${raiz()}cart.js`, { headers: { Accept: 'application/json' } });
      const carrito = await carritoRespuesta.json();
      publicarCarrito(carrito);

      diferido?.resolve({
        cart: CartLinesUpdateEvent?.createCartFromAjaxResponse?.(carrito),
        detail: {
          items: carrito.items,
          itemCount: this.#cantidad,
          source: 'mb-producto-granel',
          sourceId: this.id || 'mb-producto-granel',
          productId: this.dataset.productId,
          sections: cuerpo.sections,
          didError: false,
        },
      });

      this.#anunciar('Añadido al carrito');
      this.#abrirCarrito();
    } catch (error) {
      if (error instanceof ErrorTienda) {
        // Respuesta de la tienda (agotado, límite...): se muestra y se deja el carrito como estaba
        const mensaje = error.message && error.message !== 'No se pudo añadir' ? error.message : (this.dataset.msgError ?? '');
        if (errorEnvio) errorEnvio.textContent = mensaje;
        this.#anunciar(mensaje);
        diferido?.reject(error);
        if (CartErrorEvent) {
          this.dispatchEvent(new CartErrorEvent({ error: error.message, code: 'INVALID' }));
        }
      } else {
        // Sin red o respuesta rara: se envía el formulario de forma nativa (añade y redirige al carrito)
        diferido?.reject(error);
        this.#marcarOcupado(false);
        HTMLFormElement.prototype.submit.call(form);
        return;
      }
    }

    this.#marcarOcupado(false);
  }

  // ------------------------------------------------------------- Barra fija

  #barraFija() {
    const barra = this.#barra;
    const boton = this.#boton;
    if (!barra || !boton) return;
    let pasado = false;
    let pie = false;

    const actualizar = () => {
      const visible = pasado && !pie;
      barra.toggleAttribute('data-visible', visible);
      barra.toggleAttribute('inert', !visible);
    };

    // La barra aparece cuando el botón principal se queda por encima de la pantalla
    const observador = new IntersectionObserver(
      ([entrada]) => {
        pasado = !entrada.isIntersecting && entrada.boundingClientRect.top < 0;
        actualizar();
      },
      { threshold: 0 }
    );
    observador.observe(boton);
    this.#signal.addEventListener('abort', () => observador.disconnect());

    // Y se esconde cuando el pie de página entra en pantalla
    const pieDePagina = document.querySelector('footer');
    if (pieDePagina) {
      const observadorPie = new IntersectionObserver(
        ([entrada]) => {
          pie = entrada.isIntersecting;
          actualizar();
        },
        { threshold: 0 }
      );
      observadorPie.observe(pieDePagina);
      this.#signal.addEventListener('abort', () => observadorPie.disconnect());
    }

    barra.querySelector('[data-barra-enviar]')?.addEventListener(
      'click',
      () => {
        this.#form?.requestSubmit();
      },
      { signal: this.#signal }
    );
  }
}

if (!customElements.get('mb-producto-granel')) {
  customElements.define('mb-producto-granel', MbProductoGranel);
}
